import {
    Box,
    Layers,
    LayoutDashboard,
    Settings,
    Truck
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BulkOperations } from '../components/tracking/bulk/BulkOperations';
import { TrackingOverview } from '../components/tracking/dashboard/TrackingOverview';
import { ShipmentControl } from '../components/tracking/shipment/ShipmentControl';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../hooks/useAuth';

const TrackingManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAwb, setSelectedAwb] = useState<string | undefined>(() => {
    const awbParam = searchParams.get('awb');
    return awbParam ? awbParam.trim() : undefined;
  });

  // Role-based access control
  const canManageEvents = user?.role && ['SUPERADMIN', 'MANAGER', 'OPERATION', 'SUPERVISOR'].includes(user.role);
  const canBulkUpdate = user?.role && ['SUPERADMIN', 'MANAGER', 'OPERATION'].includes(user.role);
  const canViewAnalytics = user?.role && ['SUPERADMIN', 'MANAGER', 'DIRECTOR'].includes(user.role);

  const tabOptions = useMemo(
    () => [
      { value: 'dashboard', label: 'Overview', icon: LayoutDashboard, enabled: !!canViewAnalytics },
      { value: 'shipments', label: 'Shipments', icon: Box, enabled: !!canManageEvents },
      { value: 'bulk', label: 'Bulk Ops', icon: Layers, enabled: !!canBulkUpdate },
    ],
    [canViewAnalytics, canManageEvents, canBulkUpdate],
  );

  const fallbackTab = useMemo(() => {
    const enabledTabs = tabOptions.filter((tab) => tab.enabled);
    return enabledTabs[0]?.value ?? 'dashboard';
  }, [tabOptions]);

  const getAllowedTab = useCallback(
    (value: string | null) => {
      const match = tabOptions.find((tab) => tab.value === value && tab.enabled);
      return match?.value ?? fallbackTab;
    },
    [fallbackTab, tabOptions],
  );

  const [activeTab, setActiveTab] = useState(() => getAllowedTab(searchParams.get('tab')));

  useEffect(() => {
    const nextTab = getAllowedTab(searchParams.get('tab'));
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, getAllowedTab, searchParams]);

  useEffect(() => {
    const awbParam = searchParams.get('awb');
    const normalized = awbParam ? awbParam.trim() : undefined;
    if (normalized !== selectedAwb) {
      setSelectedAwb(normalized);
    }
  }, [searchParams, selectedAwb]);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      const nextParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          nextParams.set(key, value);
        } else {
          nextParams.delete(key);
        }
      });
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleTabChange = (value: string) => {
    const nextTab = getAllowedTab(value);
    setActiveTab(nextTab);
    updateSearchParams({ tab: nextTab });
  };

  const handleNavigateToShipment = (awb: string) => {
    const normalizedAwb = awb.trim();
    if (!normalizedAwb) return;
    setSelectedAwb(normalizedAwb);
    const nextTab = getAllowedTab('shipments');
    setActiveTab(nextTab);
    updateSearchParams({ tab: nextTab, awb: normalizedAwb });
  };

  const roleLabel = user?.role ? user.role.replace('_', ' ') : 'User';

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 pb-12">
      {/* Top Navigation Bar / Header */}
      <div className="bg-white dark:bg-slate-900 border-b sticky top-0 z-30 px-6 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg text-white">
              <Truck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                Tracking Control Center
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Manage shipments, monitor events, and analyze performance
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="px-3 py-1 text-sm font-medium">
              {roleLabel}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              className="hidden md:flex"
              onClick={() => navigate('/settings')}
            >
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-8">
          
          {/* Main Navigation Tabs */}
          <div className="flex justify-center">
            <TabsList className="grid w-full max-w-3xl grid-cols-3 p-1 bg-slate-200/50 dark:bg-slate-800/50 backdrop-blur-sm rounded-xl">
              {tabOptions.map((tab) => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm rounded-lg transition-all py-2.5"
                    disabled={!tab.enabled}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {tab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {!canManageEvents && !canBulkUpdate && !canViewAnalytics ? (
             <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm text-center">
               <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-full mb-4">
                 <Settings className="h-8 w-8 text-red-500" />
               </div>
               <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Access Restricted</h3>
               <p className="text-slate-500 max-w-md mt-2">You do not have the necessary permissions to view this dashboard. Please contact your administrator.</p>
             </div>
          ) : (
            <>
              {/* Dashboard / Analytics Tab */}
              {canViewAnalytics && (
                <TabsContent value="dashboard" className="space-y-6 animate-in fade-in-50 duration-500">
                  <div className="grid gap-6">
                    <TrackingOverview onNavigateToShipment={handleNavigateToShipment} />
                  </div>
                </TabsContent>
              )}

              {/* Individual Shipment Management Tab */}
              {canManageEvents && (
                <TabsContent value="shipments" className="space-y-6 animate-in fade-in-50 duration-500">
                  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
                    <div className="mb-6">
                      <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Shipment Manager</h2>
                      <p className="text-slate-500">Search for a specific AWB to view details, update status, or add tracking events.</p>
                    </div>
                    <ShipmentControl initialAwb={selectedAwb} />
                  </div>
                </TabsContent>
              )}

              {/* Bulk Operations Tab */}
              {canBulkUpdate && (
                <TabsContent value="bulk" className="space-y-6 animate-in fade-in-50 duration-500">
                  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6">
                    <div className="mb-6">
                      <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Bulk Operations</h2>
                      <p className="text-slate-500">Update multiple shipments simultaneously or upload CSV files for mass processing.</p>
                    </div>
                    <BulkOperations userRole={user?.role || ''} />
                  </div>
                </TabsContent>
              )}
            </>
          )}

        </Tabs>
      </div>
    </div>
  );
};

export default TrackingManagement;
